import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { DollarSign, X, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const Prices = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [priceImages, setPriceImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchPriceImages();
  }, [language]);

  const fetchPriceImages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-lists?language=${language}`);
      setPriceImages(response.data);
    } catch (err) {
      console.error('Error fetching price images:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    return category === 'rental' 
      ? (language === 'de' ? 'Vermietung' : 'Rental')
      : (language === 'de' ? 'Kurse' : 'Courses');
  };

  const getSportLabel = (sport) => {
    const labels = {
      'windsurfing': t('nav.windsurfing'),
      'kitesurfing': t('nav.kitesurfing'),
      'wingfoiling': t('nav.wingfoiling'),
      'general': language === 'de' ? 'Allgemein' : 'General'
    };
    return labels[sport] || sport;
  };

  const groupedImages = priceImages.reduce((acc, img) => {
    const key = `${img.category}-${img.sport}`;
    if (!acc[key]) {
      acc[key] = {
        category: img.category,
        sport: img.sport,
        images: []
      };
    }
    acc[key].images.push(img);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" data-testid="prices-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-emerald-600 to-teal-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <DollarSign className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('prices.title')}</h1>
          <p className="text-xl text-emerald-100">{t('prices.subtitle')}</p>
        </div>
      </section>

      {/* Price Lists */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              <p className="text-xl text-gray-600 mt-4">
                {language === 'de' ? 'Preislisten werden geladen...' : 'Loading price lists...'}
              </p>
            </div>
          ) : Object.keys(groupedImages).length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <DollarSign className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-xl text-gray-600">
                {language === 'de' ? 'Preislisten werden bald verfügbar sein.' : 'Price lists will be available soon.'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedImages).map(([key, group]) => (
                <div key={key} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-500 text-white px-6 py-4">
                    <h2 className="text-xl md:text-2xl font-bold text-center">
                      {getSportLabel(group.sport)} - {getCategoryLabel(group.category)}
                    </h2>
                  </div>
                  
                  {/* Images Grid - Compact Layout */}
                  <div className="p-4 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {group.images.map((img) => (
                        <div 
                          key={img.id} 
                          className="relative group cursor-pointer"
                          onClick={() => setSelectedImage(img)}
                        >
                          <div className="relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-emerald-500 transition-all">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                              alt={`${getCategoryLabel(group.category)} - ${getSportLabel(group.sport)}`}
                              className="w-full h-auto"
                            />
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="bg-white rounded-full p-3">
                                  <ZoomIn className="w-6 h-6 text-emerald-600" />
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-center text-xs text-gray-500 mt-2">
                            {language === 'de' ? 'Klicken zum Vergrößern' : 'Click to enlarge'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          {!loading && Object.keys(groupedImages).length > 0 && (
            <div className="mt-12 text-center">
              <a
                href="https://reservations.surfmotion.com/reservation"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-now-button"
                className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
              >
                <span>{t('home.hero_cta')}</span>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${selectedImage.url}`}
              alt={selectedImage.original_filename}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-sm">
              {language === 'de' ? 'Klicken zum Schließen' : 'Click to close'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prices;